import { ChartFrame, Legend, LineLayer, valueDomain, ZeroLine } from '@finsim/charts';
import {
  MAX_INVESTMENTS,
  type Investment,
  type InvestmentId,
  type Item,
  type Scenario,
} from '@finsim/model';
import { useUnit } from 'effector-react';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { commandIssued } from '../../../entities/project';
import type { InvestmentReturns, ScenarioResult } from '../../../entities/simulation';
import {
  $hiddenScenarios,
  $shownInvestment,
  investmentSelected,
  scenarioVisibilityToggled,
} from '../../../entities/view-settings';
import { newId } from '../../../shared/lib/id';
import { withOptional } from '../../../shared/lib/with-optional';
import {
  Button,
  EditOnly,
  IconButton,
  Segmented,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '../../../shared/ui';
import { scenarioColor, seriesColor } from '../lib/series';
import { ChartCard } from './chart-card';
import { SeriesChart } from './series-chart';
import { Tile } from './kpi-strip';
import type { CanvasData } from './use-canvas';

/** The IRR axis stops here: right after buying, "if sold now" returns can run to thousands of %. */
const IRR_AXIS = [-0.5, 0.5] as const;

const finite = (v: number | undefined): v is number => v !== undefined && Number.isFinite(v);

/** Profit, IRR and multiple of an investment "if sold in month t", per variant. */
export function Returns({ data }: { data: CanvasData }) {
  const { project } = data;
  const [shownId, select] = useUnit([$shownInvestment, investmentSelected]);
  // The view shows only while the project has an investment (visibleViews).
  const investment = project.investments.find((i) => i.id === shownId);
  if (!investment) return null;
  return (
    <div className="space-y-4 p-4">
      <InvestmentHeader data={data} investment={investment} onSelect={select} />
      <ReturnsCharts data={data} investment={investment} />
    </div>
  );
}

function InvestmentHeader({
  data,
  investment,
  onSelect,
}: {
  data: CanvasData;
  investment: Investment;
  onSelect: (id: InvestmentId) => void;
}) {
  const { t } = useTranslation();
  const issue = useUnit(commandIssued);
  const { project, focused } = data;
  const update = (next: Investment, field: string): void => {
    issue({
      command: { type: 'investment/update', investment: next },
      coalesceKey: `${investment.id}:${field}`,
    });
  };
  const scenario = project.scenarios.find((s) => s.id === focused?.id);
  const items = (scenario?.itemIds ?? []).flatMap((id) => {
    const item = project.items[id];
    return item ? [item] : [];
  });
  const tag = (item: Item, on: boolean): void => {
    issue({
      command: {
        type: 'item/update',
        item: withOptional(item, 'investment', on ? investment.id : undefined),
      },
      coalesceKey: `${item.id}:investment`,
    });
  };

  return (
    <section
      aria-label={t('returns.settings')}
      className="space-y-3 rounded-lg border border-border-subtle bg-bg-1 px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        {project.investments.length > 1 && (
          <Select
            aria-label={t('returns.investment')}
            value={investment.id}
            options={project.investments.map((i) => ({ value: i.id, label: i.name }))}
            onValueChange={onSelect}
          />
        )}
        <TextField
          aria-label={t('returns.name')}
          className="min-w-40 flex-1 [&_input]:h-8"
          value={investment.name}
          onCommit={(name) => {
            update({ ...investment, name }, 'name');
          }}
        />
        <Segmented
          label={t('returns.scope')}
          value={investment.scope}
          onValueChange={(scope) => {
            update({ ...investment, scope }, 'scope');
          }}
          options={[
            { value: 'variant', label: t('returns.scopes.variant') },
            { value: 'items', label: t('returns.scopes.items') },
          ]}
        />
        <EditOnly>
          {project.investments.length < MAX_INVESTMENTS && (
            <Tooltip content={t('returns.add')}>
              <IconButton
                label={t('returns.add')}
                onClick={() => {
                  const id = newId('inv') as InvestmentId;
                  issue({
                    command: {
                      type: 'investment/add',
                      investment: {
                        id,
                        name: t('returns.newName', { n: project.investments.length + 1 }),
                        scope: 'items',
                      },
                    },
                  });
                  onSelect(id);
                }}
              >
                <Plus />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 />}
            className="hover:text-negative"
            onClick={() => {
              issue({ command: { type: 'investment/remove', id: investment.id } });
            }}
          >
            {t('returns.remove')}
          </Button>
        </EditOnly>
      </div>
      <p className="text-xs text-text-3">
        {investment.scope === 'variant'
          ? t('returns.scopeHints.variant')
          : t('returns.scopeHints.items', { name: focused?.name ?? '' })}
      </p>
      {investment.scope === 'items' && (
        <ul aria-label={t('returns.itemsLabel')} className="flex flex-wrap gap-x-5 gap-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <label className="flex items-center gap-2 text-sm text-text-2">
                <Switch
                  checked={item.investment === investment.id}
                  onCheckedChange={(on) => {
                    tag(item, on);
                  }}
                />
                {item.name}
              </label>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReturnsCharts({ data, investment }: { data: CanvasData; investment: Investment }) {
  const { t } = useTranslation();
  const [hidden, toggle] = useUnit([$hiddenScenarios, scenarioVisibilityToggled]);
  const [hover, setHover] = useState(-1);
  const { result, scenarios, visible, focused, valueMode, dates, range, buckets, fmt } = data;

  const lines = useMemo(
    () =>
      visible.flatMap((s) => {
        const returns = result.scenarios[s.id]?.investments[investment.id];
        return returns ? [{ scenario: s, returns }] : [];
      }),
    [visible, result, investment.id],
  );
  const profitOf = useCallback(
    (r: ScenarioResult) => r.investments[investment.id]?.profit,
    [investment.id],
  );
  const irrOf = useCallback(
    (r: ScenarioResult) => r.investments[investment.id]?.irr,
    [investment.id],
  );
  const own = focused ? result.scenarios[focused.id]?.investments[investment.id] : undefined;
  const at = range[1];
  const atDate = dates[at];
  const rate = (v: number | undefined): string => (finite(v) ? fmt.rate(v) : '–');

  const irrDomain = useMemo((): [number, number] => {
    const [lo, hi] = valueDomain(
      lines.map((l) => l.returns.irr[valueMode]),
      { range },
    );
    return [Math.max(lo, IRR_AXIS[0]), Math.min(hi, IRR_AXIS[1])];
  }, [lines, valueMode, range]);

  const legend = (
    <Legend
      label={t('charts.legend')}
      items={scenarios.map((s) => ({
        id: s.id,
        label: s.name,
        color: scenarioColor(s),
        hidden: hidden.has(s.id),
      }))}
      onToggle={(id) => {
        toggle(id as Scenario['id']);
      }}
    />
  );

  const perVariantTable = (title: string, value: (r: InvestmentReturns, t: number) => string) => ({
    caption: title,
    columns: [t('charts.year'), ...lines.map((l) => l.scenario.name)],
    rows: buckets.map((b) => ({
      label: String(b.year),
      values: lines.map((l) => value(l.returns, b.to)),
    })),
  });

  const soldDetail = (returns: InvestmentReturns): string => {
    const date = atDate ? fmt.month(atDate) : '';
    return Math.abs(returns.equity.nominal[at] ?? 0) < 0.005
      ? t('returns.afterSale', { date })
      : t('returns.ifSoldIn', { date });
  };

  const profitTitle = t('returns.profitTitle');
  const irrTitle = t('returns.irrTitle');
  const moneyTitle = t('returns.moneyTitle');

  return (
    <>
      {own && focused && (
        <div
          className="grid grid-cols-2 gap-3 xl:grid-cols-4"
          role="group"
          aria-label={t('returns.kpiLabel', { name: focused.name })}
        >
          <Tile
            label={t('returns.irr')}
            hint={t('returns.irrHint')}
            value={rate(own.irr[valueMode][at])}
            detail={soldDetail(own)}
          />
          <Tile
            label={t('returns.profit')}
            hint={t('returns.profitHint')}
            value={fmt.money(own.profit[valueMode][at] ?? 0)}
            detail={soldDetail(own)}
            tone={(own.profit[valueMode][at] ?? 0) < 0 ? 'negative' : undefined}
          />
          <Tile
            label={t('returns.multiple')}
            hint={t('returns.multipleHint')}
            value={
              finite(own.multiple[valueMode][at])
                ? fmt.multiple(own.multiple[valueMode][at] ?? 0)
                : '–'
            }
            detail={t('returns.multipleDetail')}
          />
          <Tile
            label={t('returns.peak')}
            hint={t('returns.peakHint')}
            value={fmt.money(own.peakInvested[valueMode][at] ?? 0)}
            detail={t('returns.peakDetail')}
          />
        </div>
      )}

      <ChartCard
        title={profitTitle}
        subtitle={data.moneyCaption}
        legend={legend}
        table={perVariantTable(profitTitle, (r, i) => fmt.money(r.profit[valueMode][i] ?? 0))}
      >
        <SeriesChart
          data={data}
          title={profitTitle}
          select={profitOf}
          height={260}
          hover={hover}
          onHover={setHover}
          showEvents
          explain={false}
          ariaLabel={t('returns.profitAria', {
            values: lines
              .map((l) => `${l.scenario.name}: ${fmt.money(l.returns.profit[valueMode][at] ?? 0)}`)
              .join('; '),
          })}
        />
      </ChartCard>

      <ChartCard
        title={irrTitle}
        subtitle={data.moneyCaption}
        table={perVariantTable(irrTitle, (r, i) => rate(r.irr[valueMode][i]))}
      >
        <SeriesChart
          data={data}
          title={irrTitle}
          select={irrOf}
          height={200}
          hover={hover}
          onHover={setHover}
          format={rate}
          formatY={fmt.percent}
          yDomain={irrDomain}
          explain={false}
          ariaLabel={t('returns.irrAria', {
            values: lines
              .map((l) => `${l.scenario.name}: ${rate(l.returns.irr[valueMode][at])}`)
              .join('; '),
          })}
        />
      </ChartCard>

      {own && focused && (
        <ChartCard
          title={moneyTitle}
          subtitle={[focused.name, data.moneyCaption].filter(Boolean).join(' · ')}
          legend={
            <Legend
              label={moneyTitle}
              items={[
                { id: 'cumulative', label: t('returns.cumulative'), color: seriesColor(0) },
                { id: 'equity', label: t('returns.equity'), color: seriesColor(1) },
              ]}
            />
          }
          table={{
            caption: moneyTitle,
            columns: [t('charts.year'), t('returns.cumulative'), t('returns.equity')],
            rows: buckets.map((b) => ({
              label: String(b.year),
              values: [
                fmt.money(own.cumulative[valueMode][b.to] ?? 0),
                fmt.money(own.equity[valueMode][b.to] ?? 0),
              ],
            })),
          }}
        >
          <ChartFrame
            markers={data.markers}
            dates={dates}
            range={range}
            height={220}
            domainSeries={[own.cumulative[valueMode], own.equity[valueMode]]}
            formatY={fmt.compact}
            formatXTick={fmt.year}
            ariaLabel={t('returns.moneyAria', {
              name: focused.name,
              cumulative: fmt.money(own.cumulative[valueMode][at] ?? 0),
              equity: fmt.money(own.equity[valueMode][at] ?? 0),
            })}
            hoverIndex={hover}
            onHoverChange={setHover}
            renderTooltip={(i) => (
              <div className="space-y-1">
                <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
                {(
                  [
                    ['cumulative', own.cumulative, 0],
                    ['equity', own.equity, 1],
                  ] as const
                ).map(([key, pair, slot]) => (
                  <p key={key} className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-1.5 text-text-2">
                      <span
                        aria-hidden
                        className="h-0.5 w-2.5 rounded-full"
                        style={{ background: seriesColor(slot) }}
                      />
                      {t(`returns.${key}`)}
                    </span>
                    <span className="tabular font-medium">
                      {fmt.money(pair[valueMode][i] ?? 0)}
                    </span>
                  </p>
                ))}
              </div>
            )}
          >
            <ZeroLine />
            <LineLayer values={own.cumulative[valueMode]} color={seriesColor(0)} />
            <LineLayer values={own.equity[valueMode]} color={seriesColor(1)} />
          </ChartFrame>
        </ChartCard>
      )}
    </>
  );
}
